import {
  DashboardOutlined,
  GiftOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Menu } from 'antd';
import {
  MenuItemType as AntMenuItemType,
  SubMenuType as AntSubMenuType,
  MenuDividerType,
  MenuItemGroupType,
} from 'antd/es/menu/hooks/useItems';
import clsx from 'classnames';
import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, LinkProps, matchPath, useLocation } from 'react-router-dom';

import {
  useOrganization,
  useOrganizationSettings,
} from '../../../../components/OrganizationProvider';
import { OrganizationType } from '../../../../graphql.generated';
import paths from '../../../../paths.json';
import { isDarkColor } from '../../../../utils';

import styles from './styles.module.less';

const fallbackColor = '#0082ff';

type MenuItemType = AntMenuItemType & {
  to?: LinkProps['to'];
};

type SubMenuType = Omit<AntSubMenuType, 'children'> & {
  children: ItemType[];
};

type ItemType =
  | MenuItemType
  | SubMenuType
  | MenuItemGroupType
  | MenuDividerType;

type SideMenuProps = {
  collapsed?: boolean;
};

const SideMenu: FC<SideMenuProps> = ({ collapsed }) => {
  const { pathname } = useLocation();
  const { type } = useOrganization();

  const [openKeys, setOpenKeys] = useState<string[] | undefined>();

  const organizationSettings = useOrganizationSettings();

  const backgroundColor = organizationSettings.primaryColor || fallbackColor;

  useEffect(() => {
    setOpenKeys([]);
  }, [pathname]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--menu-dark-item-active-bg',
      backgroundColor,
    );
  }, [backgroundColor, organizationSettings.primaryColor]);

  const selectedKeys = Object.values(paths).filter((path) =>
    matchPath(pathname, { path }),
  );

  const items = useMemo(() => {
    const transform = (item: ItemType | null): ItemType | null => {
      if (item) {
        if ('to' in item) {
          return {
            ...item,
            key: (item.to as string) || item.key,
            label: item.to ? (
              <Link to={item.to}>{item.label}</Link>
            ) : (
              item.label
            ),
          };
        }

        if ('children' in item && 'icon' in item) {
          return {
            ...item,
            children: item.children.map(transform),
          };
        }

        if ('type' in item && item.type === 'divider') {
          return {
            ...item,
            className: styles.divider,
          };
        }
      }

      return item;
    };

    return (
      [
        {
          icon: <DashboardOutlined />,
          label: 'Dashboard',
          to: paths.dashboard,
        },
        {
          icon: <GiftOutlined />,
          label: type === OrganizationType.Search ? 'Feeds' : 'Offers',
          to: paths.offers,
        },
        {
          icon: <TableOutlined />,
          key: 'reports',
          label: 'Reports',
          to: paths.customReport,
        },
      ] as ItemType[]
    )
      .map(transform)
      .filter(Boolean) as ItemType[];
  }, [type]);

  const menu = useMemo(() => {
    return (
      <Menu
        builtinPlacements={{
          rightTop: {
            overflow: {
              adjustX: 1,
              adjustY: 1,
              alwaysByViewport: true,
            },
            points: ['tl', 'tr'],
          },
        }}
        className={clsx(styles.menu, {
          [styles.invert]: !isDarkColor(backgroundColor),
        })}
        data-popup-container
        items={items}
        mode="vertical"
        onOpenChange={(keys) => {
          setOpenKeys(keys.slice(-1));
        }}
        openKeys={openKeys}
        selectedKeys={selectedKeys}
        theme="dark"
        triggerSubMenuAction={collapsed ? 'hover' : 'click'}
      />
    );
  }, [backgroundColor, collapsed, items, openKeys, selectedKeys]);

  return <>{menu}</>;
};

SideMenu.displayName = 'SideMenu';

export default SideMenu;
